// 单例运行时：登录 API、会话、以及 Vue2 的会话订阅 mixin（替代 Vue3 的 useSession）。
import { config } from './config'
import { createAuthApi } from './auth-api'
import { AuthSession } from './auth-session'
import { createProfilesApi } from './profiles-api'
import { createBusinessApi } from './business-api'
import { createBrowserLoginState, applyBrowserSessionEvent } from './browser-login-state'
import { createPhotoDrafts, indexedPhotoStorage } from './photo-drafts'
let browserState = null
let tabStorage = null
try { tabStorage = window.sessionStorage } catch (_) { /* Optional for ordinary restoration. */ }
try { browserState = createBrowserLoginState(window.localStorage, tabStorage) } catch (_) { /* Cookie restoration needs no web storage. */ }

export const authApi = createAuthApi(config.authApiBase, config.development)

export const authSession = new AuthSession(
  authApi,
  createProfilesApi(config.authApiBase, config.development),
  createBusinessApi(config.authApiBase, config.development),
  Date.now, browserState,
)

let privatePhotoStorage = null
try { privatePhotoStorage = window.indexedDB } catch (_) { /* Preview still works when browser storage is unavailable. */ }
export const photoDrafts = createPhotoDrafts(indexedPhotoStorage(privatePhotoStorage))
let draftOwner = null
authSession.subscribe(view => {
  const next = view.user ? view.user.id : ''
  // The initial signed-out snapshot precedes cookie restoration; don't erase drafts on reload.
  if (draftOwner === null && !next) return
  if (draftOwner === next) return
  draftOwner = next
  photoDrafts.setOwner(next).catch(() => {})
})

if (typeof window !== 'undefined') window.addEventListener('storage', event => {
  if (applyBrowserSessionEvent(authSession, event) && browserState && !browserState.blocked()) authSession.restore()
})

// Mobile back/forward caches may freeze a page before it receives storage events.
async function syncBrowserSession() {
  if (!browserState || document.hidden) return
  if (browserState.blocked()) {
    if (authSession.snapshot().user) authSession.clear('已退出登录。')
    return
  }
  if (browserState.changed()) {
    authSession.clear('登录状态已更新，请重新登录。')
    await authSession.restore()
  }
}
if (typeof window !== 'undefined') {
  window.addEventListener('pageshow', syncBrowserSession)
  window.addEventListener('focus', syncBrowserSession)
  document.addEventListener('visibilitychange', syncBrowserSession)
}

// Dev bypass: when VUE_APP_DEV_AUTH_BYPASS=true, inject a synthetic session so private/business
// pages render as if logged in. The token must match what the backend reads from AUTH_DEV_MOCK_TOKEN.
// Both backend and frontend env vars are expected to be left unset in production builds.
if (process.env.NODE_ENV !== 'production' && process.env.VUE_APP_DEV_AUTH_BYPASS === 'true') {
  authSession.applyDevMock({
    user: { id: 'dev-user', phoneMasked: '176****0000' },
    accessToken: process.env.VUE_APP_DEV_AUTH_TOKEN || 'dev-mock-token',
    tokenType: 'Bearer',
    expiresAt: '2099-12-31T23:59:59Z',
  })
}

/** 在组件中混入后可通过 this.session.user / this.session.notice 使用。 */
export const sessionMixin = {
  data() {
    return { session: authSession.snapshot() }
  },
  created() {
    this.__unsubscribeSession = authSession.subscribe((view) => {
      this.session = view
    })
  },
  beforeDestroy() {
    if (this.__unsubscribeSession) this.__unsubscribeSession()
  },
}

export default authSession
